/**
 * /manage/apps — running web apps (the webgen apps lane).
 *
 * Sibling of /manage/sites: a site is files on disk, an app is a container
 * with storage and its own API, so this page shows the things only a running
 * thing has — status, whether it is warm, when it was last touched — and the
 * lifecycle verbs. Those verbs live in Manage and NOT on the agent's MCP
 * surface on purpose: pausing an app breaks a live URL someone else may be
 * using, so an operator asks for it.
 *
 * Everything proxies through /admin/webgen/apps — webgen is the
 * system-of-record, CD stores nothing. On a deployment without webgen (or with
 * the apps lane off) the list call 404s and the page renders a quiet note.
 *
 * The list deliberately excludes archived apps (webgen filters them out): an
 * archived app's address 404s while its bundle and data are retained, so it
 * is gone from the operator's fleet view but not deleted.
 */

import { ApiError } from "@chai/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { Archive, ExternalLink, Pause, Play, RotateCw } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { useApi } from "@/api";
import { PageHeader } from "@/components/manage/PageHeader";
import { EmptyRow, Section } from "@/components/manage/Section";
import { StatStrip } from "@/components/manage/StatStrip";
import { StatusBadge } from "@/components/manage/StatusBadge";
import { useIsDemoManage } from "@/components/manage/org-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

export const Route = createFileRoute("/manage/apps/")({
  component: AppsPage,
});

export type AppAccess = "public" | "link" | "space";

/** webgen's app "brief" — one list row (GET /admin/webgen/apps). */
export interface WebgenAppRow {
  id: string;
  slug: string;
  title: string;
  /** draft · deploying · running · idle · paused · error (archived is filtered out). */
  status: string;
  access: AppAccess;
  version: number;
  /** A container is up right now. Idle apps are cold and wake on request. */
  warm: boolean;
  /** True when the app declares any schedule. A SERVICE app is pinned warm —
   *  never idle-reaped, restarted if it exits — and counted against the
   *  workspace's `max_service_apps`. Absent on a webgen that predates F2. */
  service?: boolean;
  /** The app's DATA version — the change token `/_chai/version` answers and
   *  `chai.live` follows. Not `version`, which is the deployed code. */
  data_version?: number;
  url: string;
  /** The shareable /s/{slug} wrapper page. */
  share_url: string;
  routes: string[];
  blueprint: string | null;
  workspace_id: string | null;
  canvas_id: string | null;
  cd_artifact_id: string | null;
  error: string | null;
  last_activity: string | null;
  updated_at: string;
  /** Phase D: what the app declared. Absent on a webgen that predates the
   *  integrations lane, which is why both are optional rather than empty
   *  arrays — "not supported here" and "none declared" look different. */
  webhooks?: AppWebhook[];
  crons?: AppCron[];
}

/** A webhook the app declares in `webgen.yaml` (phase D). `url` is the
 *  address the sender posts to; `verify` names the signature scheme it must
 *  sign with; `signing_secret_hint` is the only part of the secret webgen
 *  keeps — the value itself is shown ONCE, at rotation. */
export interface AppWebhook {
  path: string;
  url: string;
  /** The signature scheme the sender must use — `slack`, `twilio`, `shopify`,
   *  `standard`, `bearer`, `basic`, `jwt-hs256`, `graph`, the two raw HMACs,
   *  or `none`. It is what the sender has to be TOLD, so it rides on the chip
   *  beside the address rather than in a manifest somebody has to read. */
  verify: string;
  /** How old a signed delivery may be before it is refused as a replay, in
   *  seconds. Absent on a webgen that predates the scheme lane. */
  tolerance_s?: number | null;
  signing_secret_hint: string | null;
}

/** A schedule the app declares in `webgen.yaml` (phase D). */
export interface AppCron {
  name: string;
  /** Cron expression. The platform's floor is one run a minute — and any
   *  schedule at all makes the app a service app, pinned warm. */
  schedule: string;
  /** Wall clock for one run, resolved (the manifest's `budget_s`, or this
   *  deployment's default). Absent on a webgen that predates F2. */
  budget_s?: number;
  last_run_at: string | null;
  /** null when the app is paused, or when `schedule` doesn't parse — in which
   *  case webgen explains why in `error`. */
  next_run_at: string | null;
  enabled: boolean;
  error?: string | null;
}

export interface WebgenBlueprint {
  id: string;
  version: string;
  phase?: string;
  summary?: string;
}

/** Status → chip tone. "idle" is the healthy resting state, not a warning:
 *  an idle app has no container burning memory and wakes on the next request. */
export const APP_STATUS_TONE: Record<string, "ready" | "pending" | "failed" | "neutral" | "info"> =
  {
    running: "ready",
    idle: "info",
    deploying: "pending",
    draft: "neutral",
    paused: "pending",
    error: "failed",
    archived: "neutral",
  };

export const ACCESS_TONE = {
  public: "ready",
  link: "info",
  space: "neutral",
} as const;

/** The six ceilings webgen bounds a workspace with (phase E; `max_service_apps`
 *  arrived with service apps in F2). */
export const QUOTA_FIELDS = [
  "max_apps",
  "max_warm",
  "storage_mb",
  "egress_rpm",
  "max_sites",
  "max_service_apps",
] as const;

export type QuotaField = (typeof QUOTA_FIELDS)[number];

/** `GET /admin/webgen/usage?workspace_id=` — what one workspace is using.
 *  Every ceiling arrives as `{used, quota}` with `quota: 0` meaning "no
 *  ceiling", so the meter draws no bar rather than dividing by zero. */
export interface WebgenUsage {
  workspace_id: string;
  window_h: number;
  apps: {
    total: number;
    running: number;
    idle: number;
    paused: number;
    error: number;
    draft: number;
    deploying: number;
    archived: number;
    quota: number;
  };
  sites: { total: number; quota: number };
  storage_mb: { used: number; quota: number };
  warm: { used: number; quota: number };
  /** Apps with a schedule. They are pinned warm and never idle-stop, so this
   *  is memory the box does not get back — metered per workspace AND across
   *  the whole deployment (`fleet_*`, WEBGEN_SERVICE_BUDGET). */
  service: { used: number; quota: number; fleet_used: number; fleet_quota: number };
  egress_24h: { calls: number; refused: number; rpm_quota: number };
  webhooks_24h: { received: number; delivered: number; rejected: number };
  cron_24h: { runs: number; errors: number };
  quota: Record<QuotaField, number> & { overrides: QuotaField[] };
}

/** `GET /admin/webgen/workspaces/{id}/quota`. `override` is null when the
 *  workspace tracks the deployment on every ceiling; a field inside it is
 *  null when only THAT ceiling tracks the deployment. */
export interface WebgenQuota {
  workspace_id: string;
  quota: Record<QuotaField, number> & { overrides: QuotaField[] };
  defaults: Record<QuotaField, number> & { overrides: QuotaField[] };
  override:
    | (Record<QuotaField, number | null> & {
        note: string | null;
        updated_by: string | null;
        updated_at: string | null;
      })
    | null;
}

export const APPS_KEY = ["admin-webgen-apps"] as const;
const BLUEPRINTS_KEY = ["admin-webgen-blueprints"] as const;
const WORKSPACES_KEY = ["admin-webgen-workspaces"] as const;
const USAGE_KEY = ["admin-webgen-usage"] as const;
const QUOTA_KEY = ["admin-webgen-quota"] as const;

/** Label + one-line meaning for each ceiling, in the order they are shown. */
const QUOTA_LABELS: Record<QuotaField, { label: string; hint: string }> = {
  max_apps: { label: "Apps", hint: "non-archived apps in this workspace" },
  max_warm: { label: "Warm", hint: "containers up at once — idle apps are free" },
  storage_mb: { label: "Storage", hint: "kv + documents + blobs across its apps" },
  egress_rpm: { label: "Egress", hint: "outbound requests a minute, all apps" },
  max_sites: { label: "Sites", hint: "published static artifacts" },
  max_service_apps: {
    label: "Service apps",
    hint: "apps with a schedule — pinned warm, they never idle",
  },
};

/** A used/limit bar. `limit` of 0 means no ceiling, and then there is nothing
 *  to fill — showing an empty track would read as "0% of something". */
function Meter({
  label,
  hint,
  used,
  limit,
  unit = "",
  overridden = false,
}: {
  label: string;
  hint: string;
  used: number;
  limit: number;
  unit?: string;
  overridden?: boolean;
}) {
  const uncapped = limit <= 0;
  const pct = uncapped ? 0 : Math.min(100, Math.round((used / limit) * 100));
  // 90% is "act this week", 100% is "it is refusing calls right now".
  const tone =
    pct >= 100
      ? "var(--color-semantic-error)"
      : pct >= 90
        ? "var(--color-semantic-warning)"
        : "var(--color-primary)";
  return (
    <div className="px-4 py-3">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-medium text-foreground">
          {label}
          {overridden && (
            <span
              title="This workspace has its own ceiling here"
              className="ml-1.5 align-middle font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground"
            >
              set
            </span>
          )}
        </span>
        <span className="font-mono text-xs tabular-nums text-muted-foreground">
          {used.toLocaleString()}
          {unit}
          {uncapped ? (
            <span className="ml-1">/ no ceiling</span>
          ) : (
            <span className="ml-1">
              / {limit.toLocaleString()}
              {unit}
            </span>
          )}
        </span>
      </div>
      <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-[var(--color-surface-soft)]">
        {!uncapped && (
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${Math.max(pct, used > 0 ? 2 : 0)}%`, background: tone }}
            // Decorative: the used / limit figures are printed beside it.
            aria-hidden="true"
          />
        )}
      </div>
      <p className="mt-1.5 text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

/** One 24-hour activity line: a total, and the part of it that went wrong. */
function ActivityCell({
  label,
  total,
  bad,
  badLabel,
}: {
  label: string;
  total: number;
  bad: number;
  badLabel: string;
}) {
  return (
    <div className="px-4 py-3">
      <p className="font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 font-mono text-lg tabular-nums text-foreground">
        {total.toLocaleString()}
      </p>
      <p className="mt-0.5 text-xs text-muted-foreground">
        {bad > 0 ? (
          <span style={{ color: "var(--color-semantic-warning)" }}>
            {bad.toLocaleString()} {badLabel}
          </span>
        ) : (
          `no ${badLabel}`
        )}
      </p>
    </div>
  );
}

/**
 * "Usage & quotas" — the fleet view's other half.
 *
 * A quota bounds a WORKSPACE (its apps, their storage and outbound calls, its
 * sites), not an app, so this section is workspace-scoped and everything above
 * it on the page is not. The numbers are webgen's, measured from its own rows;
 * CD keeps no copy and no counter.
 *
 * The editor is deliberately tri-state and says so in one line: a blank field
 * TRACKS this deployment's default, a number pins it, and 0 removes the
 * ceiling. That is what lets an operator raise one number without freezing the
 * other four at whatever they happen to be today.
 */
function UsageAndQuotas() {
  const api = useApi();
  const qc = useQueryClient();
  const [workspace, setWorkspace] = useState<string>("");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [note, setNote] = useState("");

  const workspaces = useQuery({
    queryKey: WORKSPACES_KEY,
    queryFn: () => api.request<{ workspaces: string[] }>("/admin/webgen/usage"),
    retry: false,
  });

  const ids = workspaces.data?.workspaces ?? [];
  // Derived, not synced: the first workspace is the default until the operator
  // picks one, and a useEffect that copied it into state would just be a
  // second source of truth that can lag the list.
  const selected = workspace || ids[0] || "";

  const usage = useQuery({
    queryKey: [...USAGE_KEY, selected],
    enabled: !!selected,
    queryFn: () =>
      api.request<WebgenUsage>(`/admin/webgen/usage?workspace_id=${encodeURIComponent(selected)}`),
    retry: false,
  });

  const quota = useQuery({
    queryKey: [...QUOTA_KEY, selected],
    enabled: !!selected,
    queryFn: () =>
      api.request<WebgenQuota>(`/admin/webgen/workspaces/${encodeURIComponent(selected)}/quota`),
    retry: false,
  });

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.request<WebgenQuota>(`/admin/webgen/workspaces/${encodeURIComponent(selected)}/quota`, {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      setEditing(false);
      toast.success("Quota saved", {
        description: "It applies to the next call this workspace makes.",
      });
    },
    onError: (err: { message?: string }) => toast.error(err.message ?? "Couldn't save the quota"),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: [...QUOTA_KEY, selected] });
      void qc.invalidateQueries({ queryKey: [...USAGE_KEY, selected] });
    },
  });

  const startEditing = () => {
    const override = quota.data?.override;
    setDraft(
      Object.fromEntries(
        QUOTA_FIELDS.map((f) => [
          f,
          override?.[f] === null || override?.[f] === undefined ? "" : String(override[f]),
        ]),
      ),
    );
    setNote(quota.data?.override?.note ?? "");
    setEditing(true);
  };

  const submit = () => {
    const override = quota.data?.override;
    const body: Record<string, unknown> = {};
    const clear: string[] = [];
    for (const field of QUOTA_FIELDS) {
      const raw = (draft[field] ?? "").trim();
      if (raw === "") {
        // Blank means "track the deployment" — only worth sending when this
        // field was actually pinned before.
        if (override?.[field] !== null && override?.[field] !== undefined) {
          clear.push(field);
        }
        continue;
      }
      const n = Number(raw);
      if (!Number.isInteger(n) || n < 0) {
        toast.error(`${QUOTA_LABELS[field].label} must be a whole number of 0 or more`);
        return;
      }
      body[field] = n;
    }
    if (clear.length) body.clear = clear;
    if (note.trim() !== (quota.data?.override?.note ?? "")) body.note = note.trim();
    if (!Object.keys(body).length) {
      setEditing(false);
      return;
    }
    save.mutate(body);
  };

  if (workspaces.isError) return null;

  const overrides = new Set(quota.data?.quota.overrides ?? []);
  const u = usage.data;

  return (
    <Section
      title="Usage & quotas"
      description="What one workspace is using on this box, and the ceilings it is using it against. Webgen measures these from its own rows — nothing is counted twice."
      actions={
        ids.length > 1 ? (
          <select
            aria-label="Workspace"
            value={selected}
            onChange={(e) => {
              setWorkspace(e.target.value);
              setEditing(false);
            }}
            className="h-8 rounded-md border border-[var(--color-hairline-strong)] bg-card px-2 font-mono text-xs text-foreground"
          >
            {ids.map((id) => (
              <option key={id} value={id}>
                {id}
              </option>
            ))}
          </select>
        ) : undefined
      }
    >
      {workspaces.isLoading ? (
        <Skeleton className="h-40 w-full rounded-md" />
      ) : !ids.length ? (
        <div className="rounded-lg border border-dashed border-[var(--color-hairline)] bg-card px-4 py-10 text-center text-sm text-muted-foreground">
          No workspace has published an app or a site yet, so there is nothing to measure.
        </div>
      ) : (
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="overflow-hidden rounded-lg border border-[var(--color-hairline)] bg-card">
            {usage.isLoading || !u ? (
              <Skeleton className="h-64 w-full" />
            ) : (
              <>
                <div className="divide-y divide-[var(--color-hairline-soft)]">
                  <Meter
                    label={QUOTA_LABELS.max_apps.label}
                    hint={
                      u.apps.archived > 0
                        ? `${QUOTA_LABELS.max_apps.hint} · ${u.apps.archived} archived (kept, not counted)`
                        : QUOTA_LABELS.max_apps.hint
                    }
                    used={u.apps.total}
                    limit={u.apps.quota}
                    overridden={overrides.has("max_apps")}
                  />
                  <Meter
                    label={QUOTA_LABELS.max_warm.label}
                    hint={QUOTA_LABELS.max_warm.hint}
                    used={u.warm.used}
                    limit={u.warm.quota}
                    overridden={overrides.has("max_warm")}
                  />
                  <Meter
                    label={QUOTA_LABELS.storage_mb.label}
                    hint={QUOTA_LABELS.storage_mb.hint}
                    used={u.storage_mb.used}
                    limit={u.storage_mb.quota}
                    unit=" MB"
                    overridden={overrides.has("storage_mb")}
                  />
                  <Meter
                    label={QUOTA_LABELS.max_service_apps.label}
                    hint={
                      u.service
                        ? `${QUOTA_LABELS.max_service_apps.hint} · ${u.service.fleet_used} of ${
                            u.service.fleet_quota || "∞"
                          } across the deployment`
                        : QUOTA_LABELS.max_service_apps.hint
                    }
                    used={u.service?.used ?? 0}
                    limit={u.service?.quota ?? 0}
                    overridden={overrides.has("max_service_apps")}
                  />
                  <Meter
                    label={QUOTA_LABELS.max_sites.label}
                    hint={QUOTA_LABELS.max_sites.hint}
                    used={u.sites.total}
                    limit={u.sites.quota}
                    overridden={overrides.has("max_sites")}
                  />
                </div>
                <div className="grid grid-cols-3 divide-x divide-[var(--color-hairline-soft)] border-t border-[var(--color-hairline)] bg-[var(--color-surface-soft)]">
                  <ActivityCell
                    label={`Egress ${u.window_h}h`}
                    total={u.egress_24h.calls}
                    bad={u.egress_24h.refused}
                    badLabel="refused"
                  />
                  <ActivityCell
                    label={`Webhooks ${u.window_h}h`}
                    total={u.webhooks_24h.received}
                    bad={u.webhooks_24h.rejected}
                    badLabel="rejected"
                  />
                  <ActivityCell
                    label={`Runs ${u.window_h}h`}
                    total={u.cron_24h.runs}
                    bad={u.cron_24h.errors}
                    badLabel="errors"
                  />
                </div>
              </>
            )}
          </div>

          <div className="rounded-lg border border-[var(--color-hairline)] bg-card">
            <div className="flex items-baseline justify-between gap-2 border-b border-[var(--color-hairline-soft)] px-4 py-3">
              <h3 className="text-sm font-medium text-foreground">Ceilings</h3>
              {!editing && (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={quota.isLoading || !quota.data}
                  onClick={startEditing}
                >
                  Edit
                </Button>
              )}
            </div>
            {quota.isLoading || !quota.data ? (
              <Skeleton className="m-4 h-40 rounded-md" />
            ) : editing ? (
              <div className="px-4 py-3">
                <p className="mb-3 text-xs leading-relaxed text-muted-foreground">
                  Blank tracks this deployment's default. A number pins it for this workspace;{" "}
                  <code className="font-mono">0</code> removes the ceiling entirely.
                </p>
                <div className="space-y-2.5">
                  {QUOTA_FIELDS.map((field) => (
                    <label
                      key={field}
                      htmlFor={`quota-${field}`}
                      className="flex items-center gap-2"
                    >
                      <span className="w-24 shrink-0 text-xs text-muted-foreground">
                        {QUOTA_LABELS[field].label}
                      </span>
                      <Input
                        id={`quota-${field}`}
                        type="number"
                        min={0}
                        step={1}
                        inputMode="numeric"
                        className="h-8 font-mono text-xs"
                        placeholder={`${quota.data.defaults[field]} (default)`}
                        value={draft[field] ?? ""}
                        onChange={(e) => setDraft((d) => ({ ...d, [field]: e.target.value }))}
                      />
                    </label>
                  ))}
                  <label htmlFor="quota-note" className="flex items-center gap-2">
                    <span className="w-24 shrink-0 text-xs text-muted-foreground">Note</span>
                    <Input
                      id="quota-note"
                      className="h-8 text-xs"
                      placeholder="Why, for the next operator"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                    />
                  </label>
                </div>
                <div className="mt-4 flex items-center justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={save.isPending}
                    onClick={() => setEditing(false)}
                  >
                    Cancel
                  </Button>
                  <Button size="sm" disabled={save.isPending} onClick={submit}>
                    {save.isPending ? "Saving…" : "Save"}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <dl className="divide-y divide-[var(--color-hairline-soft)]">
                  {QUOTA_FIELDS.map((field) => {
                    const value = quota.data!.quota[field];
                    const pinned = overrides.has(field);
                    return (
                      <div
                        key={field}
                        className="flex items-baseline justify-between gap-3 px-4 py-2.5"
                      >
                        <dt className="text-xs text-muted-foreground">
                          {QUOTA_LABELS[field].label}
                        </dt>
                        <dd className="font-mono text-xs tabular-nums text-foreground">
                          {value === 0 ? "no ceiling" : value.toLocaleString()}
                          <span className="ml-1.5 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
                            {pinned ? "set" : "default"}
                          </span>
                        </dd>
                      </div>
                    );
                  })}
                </dl>
                {quota.data.override?.note && (
                  <p className="border-t border-[var(--color-hairline-soft)] px-4 py-3 text-xs leading-relaxed text-muted-foreground">
                    “{quota.data.override.note}”
                    {quota.data.override.updated_at && (
                      <span className="ml-1 whitespace-nowrap">
                        —{" "}
                        {formatDistanceToNow(new Date(quota.data.override.updated_at), {
                          addSuffix: true,
                        })}
                      </span>
                    )}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </Section>
  );
}

function AppsPage() {
  const api = useApi();
  const qc = useQueryClient();
  // Demo tenants never see this surface — the nav hides it and the API refuses
  // demo actors; a typed-in URL gets a dead end, not a pitch.
  const isDemo = useIsDemoManage();

  const apps = useQuery({
    queryKey: APPS_KEY,
    enabled: !isDemo,
    queryFn: () => api.request<WebgenAppRow[]>("/admin/webgen/apps"),
    // 404 = webgen isn't configured on this deployment — a state, not a blip.
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 2,
  });
  const notEnabled = apps.isError && apps.error instanceof ApiError && apps.error.status === 404;

  const blueprints = useQuery({
    queryKey: BLUEPRINTS_KEY,
    enabled: !isDemo && !notEnabled,
    queryFn: () => api.request<{ blueprints: WebgenBlueprint[] }>("/admin/webgen/blueprints"),
    retry: false,
  });

  const invalidate = () => void qc.invalidateQueries({ queryKey: APPS_KEY });

  const setPaused = useMutation({
    mutationFn: ({ id, paused }: { id: string; paused: boolean }) =>
      api.request<WebgenAppRow>(`/admin/webgen/apps/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ paused }),
      }),
    onSuccess: (_data, { paused }) =>
      toast.success(paused ? "App paused — its address answers a paused notice" : "App resumed"),
    onError: (err: { message?: string }) =>
      toast.error(err.message ?? "Couldn't change the app's state"),
    onSettled: invalidate,
  });

  const restart = useMutation({
    mutationFn: (id: string) =>
      api.request<WebgenAppRow>(`/admin/webgen/apps/${id}/restart`, {
        method: "POST",
      }),
    onSuccess: () => toast.success("Restarted — the next request wakes a fresh container"),
    onError: (err: { message?: string }) => toast.error(err.message ?? "Restart failed"),
    onSettled: invalidate,
  });

  const archive = useMutation({
    mutationFn: (id: string) =>
      api.request<WebgenAppRow>(`/admin/webgen/apps/${id}`, {
        method: "DELETE",
      }),
    // The server list only carries active apps, so drop the row immediately.
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: APPS_KEY });
      const prev = qc.getQueryData<WebgenAppRow[]>(APPS_KEY);
      qc.setQueryData<WebgenAppRow[]>(APPS_KEY, (old) => (old ?? []).filter((a) => a.id !== id));
      return { prev };
    },
    onError: (err: { message?: string }, _id, ctx) => {
      if (ctx?.prev) qc.setQueryData(APPS_KEY, ctx.prev);
      toast.error(err.message ?? "Archive failed");
    },
    onSuccess: () => toast.success("App archived — its bundle and data are kept"),
    onSettled: invalidate,
  });

  const rows = apps.data ?? [];
  const byStatus = (s: string) => rows.filter((a) => a.status === s).length;
  const busy = setPaused.isPending || restart.isPending || archive.isPending;

  if (isDemo) {
    return (
      <div className="mx-auto max-w-[960px] px-10 py-10 text-sm text-muted-foreground">
        Not available in this demo.
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1200px] px-10 py-10">
      <PageHeader
        eyebrow="Admin · Publishing"
        title="Apps"
        description="Web apps the agent publishes from a blueprint — a page plus its own API and storage, each in its own container. Idle apps stop themselves and wake on the next request."
      />

      {notEnabled ? (
        <Section title="Apps">
          <div className="rounded-lg border border-dashed border-[var(--color-hairline)] bg-card px-4 py-10 text-center text-sm text-muted-foreground">
            The apps lane isn't enabled on this deployment. Once webgen is configured with a
            container runtime, apps the agent publishes show up here.
          </div>
        </Section>
      ) : (
        <>
          <StatStrip
            // Four cells, because the strip lays out in four columns — and
            // "archived" cannot be one of them: webgen's list endpoint filters
            // archived apps out, so the count would be a permanent zero.
            items={[
              { label: "Running", value: byStatus("running"), hint: "warm now" },
              { label: "Idle", value: byStatus("idle"), hint: "cold, wakes on request" },
              { label: "Paused", value: byStatus("paused"), hint: "address kept, serving off" },
              { label: "Error", value: byStatus("error"), hint: "last deploy failed" },
            ]}
          />

          <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_264px]">
            <Section
              title="All apps"
              description="Archiving takes an app offline; the bundle and its stored data are kept."
            >
              {apps.isLoading ? (
                <Skeleton className="h-40 w-full rounded-md" />
              ) : apps.isError ? (
                <div className="rounded-lg border border-dashed border-[var(--color-hairline)] bg-card px-4 py-10 text-center text-sm text-muted-foreground">
                  Couldn't load apps —{" "}
                  {(apps.error as { message?: string }).message ?? "webgen didn't answer"}.
                </div>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-[var(--color-hairline)] bg-card">
                  <table className="w-full min-w-[640px] text-sm">
                    <thead className="bg-[var(--color-surface-soft)]">
                      <tr className="text-left">
                        <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                          App
                        </th>
                        <th className="w-32 px-3 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                          Blueprint
                        </th>
                        <th className="w-24 px-3 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                          Status
                        </th>
                        <th className="w-20 px-3 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                          Access
                        </th>
                        <th className="w-12 px-2 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                          Ver
                        </th>
                        <th className="w-24 px-3 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                          Activity
                        </th>
                        <th className="w-32 px-3 py-2.5" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--color-hairline-soft)]">
                      {rows.length ? (
                        rows.map((a) => {
                          const paused = a.status === "paused";
                          return (
                            <tr
                              key={a.id}
                              className="group transition hover:bg-[var(--color-surface)]"
                            >
                              <td className="max-w-64 px-4 py-3">
                                <Link
                                  to="/manage/apps/$id"
                                  params={{ id: a.id }}
                                  className="block truncate font-medium text-foreground hover:underline"
                                >
                                  {a.title}
                                </Link>
                                <code
                                  className="mt-0.5 inline-block max-w-full truncate rounded bg-[var(--color-surface-soft)] px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground"
                                  title={a.slug}
                                >
                                  {a.slug}
                                </code>
                              </td>
                              <td className="px-3 py-3 font-mono text-[11px] text-muted-foreground">
                                {a.blueprint ?? "—"}
                              </td>
                              <td className="px-3 py-3">
                                <div className="flex items-center gap-1.5">
                                  <StatusBadge
                                    label={a.status}
                                    tone={APP_STATUS_TONE[a.status] ?? "neutral"}
                                  />
                                  {a.warm && (
                                    <span
                                      title="A container is up right now"
                                      aria-label="warm"
                                      className="size-1.5 rounded-full bg-[var(--color-semantic-success)]"
                                    />
                                  )}
                                </div>
                              </td>
                              <td className="px-3 py-3">
                                <StatusBadge
                                  label={a.access}
                                  tone={ACCESS_TONE[a.access] ?? "neutral"}
                                />
                              </td>
                              <td className="px-2 py-3 font-mono text-xs tabular-nums text-muted-foreground">
                                {a.version > 0 ? `v${a.version}` : "—"}
                              </td>
                              <td className="px-3 py-3 font-mono text-xs text-muted-foreground">
                                {a.last_activity
                                  ? formatDistanceToNow(new Date(a.last_activity), {
                                      addSuffix: true,
                                    })
                                  : "never"}
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex items-center justify-end gap-0.5">
                                  <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    aria-label="Open app"
                                    title="Open the app's page"
                                    onClick={() =>
                                      window.open(
                                        a.share_url ?? a.url,
                                        "_blank",
                                        "noopener,noreferrer",
                                      )
                                    }
                                  >
                                    <ExternalLink className="size-3.5 text-muted-foreground" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    disabled={busy}
                                    aria-label={paused ? "Resume app" : "Pause app"}
                                    title={
                                      paused
                                        ? "Resume — the app serves again"
                                        : "Pause — the address answers a paused notice"
                                    }
                                    onClick={() => setPaused.mutate({ id: a.id, paused: !paused })}
                                  >
                                    {paused ? (
                                      <Play className="size-3.5 text-muted-foreground" />
                                    ) : (
                                      <Pause className="size-3.5 text-muted-foreground" />
                                    )}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    disabled={busy}
                                    aria-label="Restart app"
                                    title="Restart — stop the container; the next request wakes a fresh one"
                                    onClick={() => restart.mutate(a.id)}
                                  >
                                    <RotateCw className="size-3.5 text-muted-foreground" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    disabled={busy}
                                    aria-label="Archive app"
                                    title="Archive app"
                                    onClick={() => {
                                      if (
                                        confirm(
                                          `Archive "${a.title}"? Its address stops working. The bundle and stored data are kept.`,
                                        )
                                      ) {
                                        archive.mutate(a.id);
                                      }
                                    }}
                                  >
                                    <Archive className="size-3.5 text-destructive" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <EmptyRow colSpan={7}>
                          No apps yet — when the agent publishes one from a blueprint, it lands
                          here.
                        </EmptyRow>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>

            <Section title="Blueprints" description="What a new app can be built from.">
              <div className="rounded-lg border border-[var(--color-hairline)] bg-card">
                {blueprints.isLoading ? (
                  <Skeleton className="h-32 w-full rounded-md" />
                ) : blueprints.data?.blueprints?.length ? (
                  <ul className="divide-y divide-[var(--color-hairline-soft)]">
                    {blueprints.data.blueprints.map((bp) => (
                      <li key={`${bp.id}@${bp.version}`} className="px-4 py-3">
                        <div className="flex items-baseline justify-between gap-2">
                          <code className="font-mono text-[11px] font-medium text-foreground">
                            {bp.id}
                          </code>
                          <span className="font-mono text-[10px] text-muted-foreground">
                            v{bp.version}
                          </span>
                        </div>
                        {bp.summary && (
                          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                            {bp.summary}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="px-4 py-8 text-center text-xs text-muted-foreground">
                    No blueprints published.
                  </p>
                )}
              </div>
            </Section>
          </div>

          {/* Workspace-scoped, so it sits below the app fleet rather than
              inside it: a quota bounds everything one tenant has on the box. */}
          <UsageAndQuotas />
        </>
      )}
    </div>
  );
}
